import React, { useEffect, useState } from 'react'
import { sitesService } from '../../../../_services/sites.service'
import useSites, { groupSites } from '../../../multiLocation/useSites'

// Your brand > "Apply to all locations": copies this location's email brand
// (brand kit, logo, colour; never its photo or bio) to the other locations
// the owner picks, through the settings push (area `emailBrand`, owner only
// on the server). Same gate as ApplyToAllLocations: a group, and the owner.
// A single salon renders nothing.

export const ApplyBrandToLocations = ({ changes, flush }) => {
  const { inGroup, canAddLocation, sites, group } = useSites()
  const [confirming, setConfirming] = useState(false)
  const [picked, setPicked] = useState([])
  const [state, setState] = useState('idle')
  const [note, setNote] = useState(null)
  const [appliedAt, setAppliedAt] = useState(0)
  const others = inGroup ? groupSites({ sites, group }).filter((s) => !s.isActive) : []
  const here = sites.find((s) => s.isActive)

  useEffect(() => { if (confirming) setPicked(others.map((s) => String(s.salonId))) }, [confirming]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!inGroup || !canAddLocation || !others.length) return null
  // Offered after a save on this visit, until that save has been applied
  if (changes <= appliedAt) {
    return (state === 'done' || state === 'partial') && note
      ? <div className="brc-apply"><p className={`brc-apply-text ${state === 'partial' ? 'is-warn' : ''}`} role="status">{note}</p></div>
      : null
  }

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.concat(id)))
  const names = others.filter((s) => picked.includes(String(s.salonId))).map((s) => s.salonName)

  const run = async () => {
    setState('busy')
    setNote(null)
    try {
      // The brand as it is now, on this location's record, before copying it
      await flush()
      const r = await sitesService.pushSettings(['emailBrand'], picked)
      const name = new Map(sites.map((s) => [String(s.salonId), s.salonName]))
      const gaps = ((r && r.report) || []).filter((x) => x.skipped && x.skipped.length)
      setAppliedAt(changes)
      setConfirming(false)
      if (gaps.length) {
        setState('partial')
        setNote(gaps.map((x) => `${name.get(String(x.salonId)) || 'A location'}: ${x.skipped.map((k) => k.reason).join(', ')}`).join('. '))
      } else {
        setState('done')
        setNote(`Applied to ${names.join(', ')}.`)
      }
    } catch (e) {
      setState('error')
      setNote((e && e.message && e.message !== 'not saved') ? e.message : 'Couldn’t apply your brand just now. Try again.')
    }
  }

  if (!confirming) {
    return (
      <div className="brc-apply">
        <p className="brc-apply-text">You have {others.length + 1} locations. Use this brand at the others too?</p>
        <button type="button" className="brc-btn" onClick={() => { setState('idle'); setNote(null); setConfirming(true) }}>Apply to all locations</button>
      </div>
    )
  }

  return (
    <div className="brc-apply is-confirming" role="group" aria-label="Apply your brand to other locations">
      <p className="brc-apply-text">
        Copy the logo, colour and email look from <strong>{here ? here.salonName : 'this location'}</strong> to:
      </p>
      <ul className="brc-apply-sites">
        {others.map((s) => {
          const id = String(s.salonId)
          return (
            <li key={id}>
              <label>
                <input type="checkbox" checked={picked.includes(id)} onChange={() => toggle(id)} />
                <span>{s.salonName}</span>
              </label>
            </li>
          )
        })}
      </ul>
      <p className="brc-apply-warn">
        {names.length
          ? `This replaces the brand now set at ${names.join(', ')}. Their photos and bios stay as they are, and each can change its brand again afterwards.`
          : 'Pick at least one location.'}
      </p>
      {state === 'error' && note && <p className="brc-apply-text is-warn" role="alert">{note}</p>}
      <div className="brc-apply-actions">
        <button type="button" className="brc-btn" onClick={() => setConfirming(false)} disabled={state === 'busy'}>Cancel</button>
        <button type="button" className="brc-btn brc-btn--primary" onClick={run} disabled={!names.length || state === 'busy'}>
          {state === 'busy' ? 'Applying…' : `Replace at ${names.length} location${names.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}

export default ApplyBrandToLocations
